import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, CheckCircle } from 'lucide-react';
import apiClient from '../../utils/api';

const STATUS_OPTIONS = ['', 'PENDING', 'CONFIRMED', 'DEPARTED', 'IN_TRANSIT', 'DELIVERED', 'CANCELLED'];
const PAYMENT_STATUS_OPTIONS = ['', 'PENDING', 'COMPLETED', 'FAILED', 'REFUNDED'];
const LIMIT = 20;

export const RecoveryShipments: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [shipments, setShipments] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') ?? '');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filters: any = {};
      if (statusFilter) filters.status = statusFilter;
      if (search) filters.search = search;
      if (dateFrom) filters.dateFrom = dateFrom;
      if (dateTo) filters.dateTo = dateTo;
      const res = await apiClient.listShipments(filters, LIMIT, page * LIMIT);
      const d = res.data.data;
      let rows: any[] = d?.shipments ?? d?.rows ?? (Array.isArray(d) ? d : []);
      // client-side payment status filter (no dedicated backend param)
      if (paymentStatus) rows = rows.filter((s: any) => s.payment?.status === paymentStatus);
      setShipments(rows);
      setTotal(paymentStatus ? rows.length : (d?.total ?? d?.count ?? 0));
    } catch {} finally { setLoading(false); }
  }, [page, search, statusFilter, paymentStatus, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Shipments</h1>
      </div>

      <div className="card">
        {/* Filters row 1 */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              placeholder="Search tracking, receiver name/phone..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}
              style={{ paddingLeft: 32, width: '100%' }}
            />
          </div>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }} style={{ width: 160 }}>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s || 'All Statuses'}</option>)}
          </select>
        </div>
        {/* Filters row 2 */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <select value={paymentStatus} onChange={e => { setPaymentStatus(e.target.value); setPage(0); }} style={{ width: 180 }}>
            {PAYMENT_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s ? `Payment: ${s}` : 'All Payment Statuses'}</option>)}
          </select>
          <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(0); }} title="From date" style={{ width: 145 }} />
          <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(0); }} title="To date" style={{ width: 145 }} />
        </div>

        {loading ? <div className="loading-spinner">Loading...</div> : shipments.length === 0 ? (
          <div className="empty-state"><h3>No shipments found</h3></div>
        ) : (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Tracking #</th><th>Receiver</th><th>City</th><th>Status</th><th>Payment</th><th>Amount</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {shipments.map((s: any) => (
                    <tr key={s.id}>
                      <td><code style={{ fontSize: 11 }}>{s.trackingNumber}</code></td>
                      <td>
                        <div>{s.receiverName}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{s.receiverPhone}</div>
                      </td>
                      <td>{s.receiverCity || '—'}</td>
                      <td><span className={`badge badge-${s.status?.toLowerCase().replace(/_/g, '-')}`}>{s.status}</span></td>
                      <td>
                        {s.payment ? (
                          <span className={`badge badge-${s.payment.status?.toLowerCase()}`}>{s.payment.status}</span>
                        ) : '—'}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{Number(s.totalAmount).toLocaleString()} RWF</td>
                      <td style={{ display: 'flex', gap: 6 }}>
                        <Link to={`/recovery/shipments/${s.id}`} className="btn btn-ghost" style={{ padding: '3px 8px', fontSize: 12 }}>View</Link>
                        {['IN_TRANSIT', 'DEPARTED'].includes(s.status) && (
                          <Link to={`/recovery/shipments/${s.id}/deliver`} className="btn btn-success" style={{ padding: '3px 8px', fontSize: 12 }}>
                            <CheckCircle size={12} /> Deliver
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <button className="btn btn-ghost" onClick={() => setPage(p => p - 1)} disabled={page === 0}>Prev</button>
                <span>Page {page + 1} of {totalPages}</span>
                <button className="btn btn-ghost" onClick={() => setPage(p => p + 1)} disabled={page + 1 >= totalPages}>Next</button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
